import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  Chip,
} from "@material-ui/core";
import { Add, Edit, Delete } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { accesoClientesApi } from "src/services/accesoClientesApi";
import { useSnackbar } from "src/components/snackbar";
import { LoadingButton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  listItemInactive: {
    opacity: 0.6,
    backgroundColor: theme.palette.grey[50],
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Ingrese un email válido")
    .required("El email es requerido")
    .max(255, "El email no puede tener más de 255 caracteres"),
  nombre: Yup.string()
    .max(255, "El nombre no puede tener más de 255 caracteres")
    .nullable(),
});

const CorreosCcClienteSeleccionado = ({ value, cliente }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);

  const [cargarEmailsCC, { data: emailsCC = [], isLoading: cargandoEmailsCC }] =
    accesoClientesApi.useLazyObtenerEmailsCCQuery();
  const [actualizarEmailCC, { isLoading: actualizandoEmail }] =
    accesoClientesApi.useActualizarEmailCCMutation();
  const [agregarEmailCC, { isLoading: agregandoEmail }] =
    accesoClientesApi.useAgregarEmailCCMutation();
  const [toggleActivoEmailCC, { isLoading: toggleandoEmail }] =
    accesoClientesApi.useToggleActivoEmailCCMutation();

  const initialValues = {
    email: "",
    nombre: "",
  };

  useEffect(() => {
    if (cliente.rut && value === 4) {
      cargarEmailsCC(cliente.rut);
    }
  }, [cargarEmailsCC, cliente.rut, value]);

  const handleOpenDialog = (emailToEdit = null) => {
    setEditingEmail(emailToEdit);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEmail(null);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingEmail) {
        const payload = {
          rutCliente: cliente.rut,
          idEmailCC: editingEmail.id,
          ...values,
        };
        const res = await actualizarEmailCC(payload).unwrap();
        enqueueSnackbar(res?.message || "Email CC actualizado correctamente", {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      } else {
        const payload = {
          cliente_id: cliente.rut,
          ...values,
        };
        const res = await agregarEmailCC(payload).unwrap();
        enqueueSnackbar(res?.message || "Email CC agregado correctamente", {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }

      resetForm();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al guardar email CC:", error);
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Error al guardar el email CC",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActivo = async (emailCC, nuevoEstado) => {
    try {
      const res = await toggleActivoEmailCC({
        rutCliente: cliente.rut,
        idEmailCC: emailCC.id,
        activo: nuevoEstado,
      }).unwrap();

      enqueueSnackbar(
        res?.message ||
          `Email CC ${nuevoEstado ? "activado" : "desactivado"} correctamente`,
        {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    } catch (error) {
      console.error("Error al cambiar estado del email CC:", error);
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Error al cambiar el estado del email CC",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  };

  const emailsActivos = emailsCC.filter((email) => email.activo);
  const emailsInactivos = emailsCC.filter((email) => !email.activo);

  if (value !== 4) return null;

  return (
    <Card>
      <CardContent>
        <div className={classes.headerContainer}>
          <Typography variant="h6">
            Emails en Copia (CC)
            {emailsCC.length > 0 && (
              <Typography
                variant="caption"
                component="span"
                style={{ marginLeft: 8 }}
              >
                ({emailsActivos.length} activos, {emailsInactivos.length}{" "}
                inactivos)
              </Typography>
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            disabled={cargandoEmailsCC}
          >
            Agregar Email
          </Button>
        </div>

        {cargandoEmailsCC ? (
          <div className={classes.emptyState}>
            <Typography>Cargando emails...</Typography>
          </div>
        ) : emailsCC.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography>No hay emails en copia configurados.</Typography>
          </div>
        ) : (
          <List>
            {/* Emails Activos */}
            {emailsActivos.length > 0 && (
              <>
                {emailsActivos.map((emailCC) => (
                  <ListItem key={emailCC.id} divider>
                    <ListItemText
                      primary={
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {emailCC.email}
                          <Chip
                            label="Activo"
                            size="small"
                            color="primary"
                            className={classes.statusChip}
                          />
                        </div>
                      }
                      secondary={emailCC.nombre || "Sin nombre"}
                    />
                    <ListItemSecondaryAction>
                      <div className={classes.switchContainer}>
                        <IconButton
                          onClick={() => handleOpenDialog(emailCC)}
                          disabled={toggleandoEmail}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        {/*se usará un boton de eliminar pero que en realidad en el backend hace un cambio de estado*/}
                        <IconButton
                          onClick={() => handleToggleActivo(emailCC, false)}
                          disabled={toggleandoEmail}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </div>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </>
            )}

            {/* Emails Inactivos */}
            {emailsInactivos.length > 0 && (
              <>
                {emailsActivos.length > 0 && (
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    style={{ padding: "16px 16px 8px", fontWeight: "bold" }}
                  >
                    Emails Inactivos
                  </Typography>
                )}
                {emailsInactivos.map((emailCC) => (
                  <ListItem
                    key={emailCC.id}
                    divider
                    className={classes.listItemInactive}
                  >
                    <ListItemText
                      primary={
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {emailCC.email}
                          <Chip
                            label="Inactivo"
                            size="small"
                            variant="outlined"
                            className={classes.statusChip}
                          />
                        </div>
                      }
                      secondary={emailCC.nombre || "Sin nombre"}
                    />
                    <ListItemSecondaryAction>
                      <div className={classes.switchContainer}>
                        <IconButton
                          onClick={() => handleOpenDialog(emailCC)}
                          disabled={toggleandoEmail}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <Switch
                          checked={emailCC.activo}
                          onChange={(e) =>
                            handleToggleActivo(emailCC, e.target.checked)
                          }
                          disabled={toggleandoEmail}
                          color="secondary"
                          size="small"
                        />
                      </div>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </>
            )}
          </List>
        )}

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingEmail ? "Editar Email CC" : "Agregar Email CC"}
          </DialogTitle>

          <Formik
            initialValues={
              editingEmail
                ? {
                    email: editingEmail.email,
                    nombre: editingEmail.nombre || "",
                  }
                : initialValues
            }
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="email"
                        label="Email *"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        className={classes.formField}
                        disabled={isSubmitting}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="nombre"
                        label="Nombre (opcional)"
                        value={values.nombre}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.nombre && Boolean(errors.nombre)}
                        helperText={touched.nombre && errors.nombre}
                        className={classes.formField}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    loading={
                      isSubmitting || agregandoEmail || actualizandoEmail
                    }
                    disabled={
                      isSubmitting || agregandoEmail || actualizandoEmail
                    }
                  >
                    {isSubmitting
                      ? editingEmail
                        ? "Actualizando..."
                        : "Agregando..."
                      : editingEmail
                        ? "Actualizar"
                        : "Agregar"}
                  </LoadingButton>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CorreosCcClienteSeleccionado;
